/**
 * Typed admin (Command Center) data hooks — thin wrappers over the GENERATED Orval client functions
 * (the only sanctioned API access path), mirroring `../family/hooks.ts`. The generated functions
 * carry the real response types (from the OpenAPI response schemas), so these hooks reuse those
 * return types directly and attach a stable, structured TanStack Query key.
 *
 * Admin reads are all household-scoped by the access token — the UI never sends `householdId`. The
 * approvals queue, approve/reject mutations, members + per-child balance, and the active family goal
 * are shared with the family layer; the admin-only reads here are the economy analytics aggregate,
 * the role catalog, and the chore-template list. Mutations invalidate the relevant list on success
 * so the table + sidebar stay consistent. The money/privilege state-changes (adjust balance, grant +
 * deny redemption, place + lift hold) carry an `Idempotency-Key` so an offline replay never
 * double-applies; role creation, chore-template creation, habit creation, and achievement-bonus
 * creation also carry one (each has no natural dedupe key, so a lost-response retry would otherwise
 * mint a duplicate — a custom role/template/habit has no delete at all, and a duplicate bonus rule
 * would silently double-pay coins on every future milestone until a guardian notices and removes it)
 * — the rest (rewards catalog, economy config, household settings) are not replay-sensitive and send
 * no key.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { idempotent, type QueryOpts } from '../internal/query-utils';

import {
  authControllerInviteMemberV1,
  authControllerListInvitationsV1,
  authControllerResendInvitationV1,
  authControllerRevokeInvitationV1,
  authControllerSetChildPinV1,
  economyAdminControllerAnalyticsV1,
  economyAdminControllerAutomationV1,
  economyAdminControllerConfigV1,
  bonusesControllerListV1,
  bonusesControllerCreateV1,
  bonusesControllerUpdateV1,
  bonusesControllerRemoveV1,
  learningGoalsControllerListV1,
  learningGoalsControllerCreateV1,
  learningGoalsControllerUpdateV1,
  learningGoalsControllerRemoveV1,
  economyAdminControllerConfigureV1,
  economyAdminControllerAdjustV1,
  householdControllerGetV1,
  householdControllerUpdateV1,
  membersControllerAddChildV1,
  membersControllerAssignRolesV1,
  membersControllerDisableV1,
  membersControllerEnableV1,
  membersControllerSetLocaleV1,
  rolesControllerListV1,
  rolesControllerCreateV1,
  rolesControllerUpdateV1,
  templatesControllerListV1,
  templatesControllerCreateV1,
  templatesControllerUpdateV1,
  templatesControllerPauseV1,
  templatesControllerResumeV1,
  templatesControllerArchiveV1,
  templatesControllerRestoreV1,
  templatesControllerRemoveV1,
  rewardsControllerListV1,
  rewardsControllerCreateV1,
  rewardsControllerUpdateV1,
  rewardsControllerArchiveV1,
  rewardsControllerRestoreV1,
  rewardsControllerRemoveV1,
  rewardsControllerApproveV1,
  redemptionsControllerListV1,
  redemptionsControllerGrantV1,
  redemptionsControllerDenyV1,
  holdsControllerPlaceV1,
  holdsControllerLiftV1,
  habitsControllerListV1,
  habitsControllerGetV1,
  habitsControllerCreateV1,
  habitsControllerUpdateV1,
  habitsControllerArchiveV1,
  habitsControllerRemoveV1,
} from '../generated/endpoints';
import type {
  AddChildDto,
  AddChildResponse,
  AdjustBalanceDto,
  AssignRolesDto,
  ConfigureEconomyDto,
  CreateHabitDto,
  CreateHabitResponse,
  CreateRewardDto,
  CreateRewardResponse,
  CreateRoleDto,
  CreateRoleResponse,
  CreateTemplateDto,
  CreateTemplateResponse,
  DecideRedemptionDto,
  EconomyAnalyticsResponse,
  EconomyAutomationResponse,
  EconomyConfigResponse,
  AchievementBonusResponse,
  CreateBonusDto,
  CreateBonusResponse,
  UpdateBonusDto,
  LearningGoalResponse,
  CreateLearningGoalDto,
  CreateLearningGoalResponse,
  UpdateLearningGoalDto,
  HabitDetailResponse,
  HabitResponse,
  HabitsControllerListV1200,
  HouseholdResponse,
  InvitationListItemResponse,
  InviteMemberDto,
  InviteMemberResponse,
  ResendInvitationResponse,
  PlaceHoldDto,
  PlaceHoldResponse,
  RedemptionsControllerListV1200,
  RedemptionsControllerListV1Params,
  RewardResponse,
  RoleResponse,
  SetChildPinDto,
  SetMemberLocaleDto,
  TemplatesControllerListV1200,
  UpdateHabitDto,
  UpdateHouseholdDto,
  UpdateRewardDto,
  UpdateRoleDto,
  UpdateTemplateDto,
} from '../generated/models';
import { familyKeys, newIdempotencyKey } from '../family/hooks';

export { newIdempotencyKey };

// --- query keys (structured + stable) ---
//
// Economy/rewards reads reuse the shared `familyKeys` (balance/ledger/rewards/holds) so the admin
// surface and the family surface share one cache; the admin-only catalog reads (analytics, roles,
// templates, config, the household redemptions queue) get their own keys here.
export const adminKeys = {
  analytics: (params?: { from?: string; to?: string }) =>
    ['admin', 'economy', 'analytics', params?.from ?? 'all', params?.to ?? 'all'] as const,
  automation: () => ['admin', 'economy', 'automation'] as const,
  roles: () => ['admin', 'roles'] as const,
  templates: () => ['admin', 'templates'] as const,
  economyConfig: () => ['admin', 'economy', 'config'] as const,
  bonuses: () => ['admin', 'economy', 'bonuses'] as const,
  learningGoals: (childId: string) => ['admin', 'learning', 'goals', 'child', childId] as const,
  redemptions: () => ['admin', 'economy', 'redemptions'] as const,
  pendingRewards: () => ['economy', 'rewards', 'pending'] as const,
  rewardCatalog: () => ['economy', 'rewards', 'catalog'] as const,
  household: () => ['admin', 'household'] as const,
  invitations: () => ['admin', 'invitations'] as const,
  habits: () => ['admin', 'habits'] as const,
  habit: (habitId: string) => ['admin', 'habits', habitId] as const,
};

/**
 * Economy analytics for the admin Overview/Economy surfaces (`GET /v1/economy/analytics`): the
 * household's earn-vs-spend over time, the per-child split, and spend by reward category. Admin-tier
 * read — the API gates it; the UI only mounts it where the user has the relevant permission.
 */
export function useEconomyAnalytics(
  params?: { from?: string; to?: string },
  options?: QueryOpts<EconomyAnalyticsResponse>,
): UseQueryResult<EconomyAnalyticsResponse, Error> {
  return useQuery({
    queryKey: adminKeys.analytics(params),
    queryFn: ({ signal }) => economyAdminControllerAnalyticsV1(params, undefined, signal),
    ...options,
  });
}

/**
 * Allowance + parent-paid-interest automation status for the admin Economy screen
 * (`GET /v1/economy/automation` → `EconomyAutomationResponse`): for each payout, whether it is
 * enabled, the last run (timestamp + a cheap summary), and the next scheduled run. A READ gated at
 * `ledger:read:any` (NOT `economy:configure`), so a Relative who can see coin history can view it.
 */
export function useEconomyAutomation(
  options?: QueryOpts<EconomyAutomationResponse>,
): UseQueryResult<EconomyAutomationResponse, Error> {
  return useQuery({
    queryKey: adminKeys.automation(),
    queryFn: ({ signal }) => economyAdminControllerAutomationV1(undefined, signal),
    ...options,
  });
}

// --- roles ---

/**
 * The household's role catalog (`GET /v1/roles` → `RoleResponse[]`): built-in presets
 * (`kind: 'preset'`, `isSystem: true`) plus any custom roles, each carrying its permission codes.
 * Drives the Members "assign roles" picker and the Roles editor.
 */
export function useRoles(
  options?: QueryOpts<RoleResponse[]>,
): UseQueryResult<RoleResponse[], Error> {
  return useQuery({
    queryKey: adminKeys.roles(),
    queryFn: ({ signal }) => rolesControllerListV1(undefined, signal),
    ...options,
  });
}

export interface CreateRoleVars {
  readonly data: CreateRoleDto;
  /**
   * Stable per-intent key: a custom role has no natural dedupe key (no username) and there is no role
   * delete, so a network retry after a lost response would otherwise mint a permanent, unremovable
   * duplicate role. The route is `@Idempotent()` on the backend and requires this header.
   */
  readonly idempotencyKey: string;
}

export function useCreateRole<TContext = unknown>(
  options?: UseMutationOptions<CreateRoleResponse, Error, CreateRoleVars, TContext>,
): UseMutationResult<CreateRoleResponse, Error, CreateRoleVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data, idempotencyKey }) =>
      rolesControllerCreateV1(data, idempotent(idempotencyKey)),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: adminKeys.roles() });
      options?.onSuccess?.(...args);
    },
  });
}

export interface UpdateRoleVars {
  readonly roleId: string;
  readonly data: UpdateRoleDto;
}

export function useUpdateRole<TContext = unknown>(
  options?: UseMutationOptions<void, Error, UpdateRoleVars, TContext>,
): UseMutationResult<void, Error, UpdateRoleVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, data }) => rolesControllerUpdateV1(roleId, data),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: adminKeys.roles() });
      options?.onSuccess?.(...args);
    },
  });
}

// --- members (writes; the read lives in the shared family layer as `useMembers`) ---

export interface AddChildVars {
  readonly data: AddChildDto;
  /**
   * Stable per-intent key (ADR-0009): a child has no natural dedupe key (no username), so a network
   * retry after a lost response would otherwise mint a permanent duplicate roster row (there is no
   * member delete). The route is `@Idempotent()` on the backend and REQUIRES this header.
   */
  readonly idempotencyKey: string;
}

export function useAddChild<TContext = unknown>(
  options?: UseMutationOptions<AddChildResponse, Error, AddChildVars, TContext>,
): UseMutationResult<AddChildResponse, Error, AddChildVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data, idempotencyKey }) =>
      membersControllerAddChildV1(data, idempotent(idempotencyKey)),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: familyKeys.members() });
      options?.onSuccess?.(...args);
    },
  });
}

export interface SetChildPinVars {
  readonly childId: string;
  readonly data: SetChildPinDto;
}

/**
 * Set (or reset) a child's login PIN (`POST /v1/auth/children/:childId/pin`, admin-only
 * `member:manage`). This is the ONLY way a child gets a login credential — a child never has a
 * password. The PIN (4–6 digits, UI-validated AND API-enforced) is hashed server-side; we never read
 * it back. It is a credential, not list state, so on success there is nothing to invalidate (the
 * members roster is unchanged). The API gates the call; the UI only surfaces it where the user can
 * manage members.
 */
export function useSetChildPin<TContext = unknown>(
  options?: UseMutationOptions<void, Error, SetChildPinVars, TContext>,
): UseMutationResult<void, Error, SetChildPinVars, TContext> {
  return useMutation({
    mutationFn: ({ childId, data }) => authControllerSetChildPinV1(childId, data),
    ...options,
  });
}

/**
 * Invite a household adult — a Parent (full admin tier) or a Relative (restricted co-guardian) — via
 * the token-issuing flow (`POST /v1/auth/members` → `InviteMemberResponse`, ADR-0085). The caller
 * picks the `kind` and may attach custom roles (`roleIds`). It issues a RAW single-use `inviteToken`
 * (shown once) + its `expiresAt`, and the invitee uses it to set their password and activate. The
 * member stays inactive until they accept, so this invalidates the members list (the new row appears
 * as "disabled" until acceptance) and the pending-invitations list.
 */
export function useInviteMember<TContext = unknown>(
  options?: UseMutationOptions<InviteMemberResponse, Error, InviteMemberDto, TContext>,
): UseMutationResult<InviteMemberResponse, Error, InviteMemberDto, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => authControllerInviteMemberV1(data),
    ...options,
    onSuccess: (...args) => {
      // A new invitation creates an inactive member AND a pending-invitation row.
      void qc.invalidateQueries({ queryKey: familyKeys.members() });
      void qc.invalidateQueries({ queryKey: adminKeys.invitations() });
      options?.onSuccess?.(...args);
    },
  });
}

/**
 * The caller household's member invitations (`GET /v1/auth/members/invitations` →
 * `InvitationListItemResponse[]`, requires `member:invite`). Drives the Members "pending invite"
 * chips + resend/revoke actions. Household-scoped by the access token.
 */
export function useInvitations(
  options?: QueryOpts<InvitationListItemResponse[]>,
): UseQueryResult<InvitationListItemResponse[], Error> {
  return useQuery({
    queryKey: adminKeys.invitations(),
    queryFn: ({ signal }) => authControllerListInvitationsV1(undefined, signal),
    ...options,
  });
}

export interface InvitationVars {
  readonly invitationId: string;
}

/**
 * Resend a still-pending invitation (`POST /v1/auth/invitations/:id/resend` → a fresh one-time
 * token + extended expiry). Invalidates the invitations list so the row's expiry refreshes; the
 * member roster is unchanged (the invitee was already created on the original invite).
 */
export function useResendInvitation<TContext = unknown>(
  options?: UseMutationOptions<ResendInvitationResponse, Error, InvitationVars, TContext>,
): UseMutationResult<ResendInvitationResponse, Error, InvitationVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invitationId }) => authControllerResendInvitationV1(invitationId),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: adminKeys.invitations() });
      options?.onSuccess?.(...args);
    },
  });
}

/**
 * Revoke a still-pending invitation (`POST /v1/auth/invitations/:id/revoke`, 204). Its token stops
 * accepting and it shows as revoked. Invalidates both the invitations list (status flips to
 * revoked) and the members roster (the placeholder stays disabled).
 */
export function useRevokeInvitation<TContext = unknown>(
  options?: UseMutationOptions<void, Error, InvitationVars, TContext>,
): UseMutationResult<void, Error, InvitationVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invitationId }) => authControllerRevokeInvitationV1(invitationId),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: adminKeys.invitations() });
      void qc.invalidateQueries({ queryKey: familyKeys.members() });
      options?.onSuccess?.(...args);
    },
  });
}

export interface AssignMemberRolesVars {
  readonly memberId: string;
  readonly data: AssignRolesDto;
}

export function useAssignMemberRoles<TContext = unknown>(
  options?: UseMutationOptions<void, Error, AssignMemberRolesVars, TContext>,
): UseMutationResult<void, Error, AssignMemberRolesVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, data }) => membersControllerAssignRolesV1(memberId, data),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: familyKeys.members() });
      options?.onSuccess?.(...args);
    },
  });
}

export interface SetMemberStatusVars {
  readonly memberId: string;
}

export function useDisableMember<TContext = unknown>(
  options?: UseMutationOptions<void, Error, SetMemberStatusVars, TContext>,
): UseMutationResult<void, Error, SetMemberStatusVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId }) => membersControllerDisableV1(memberId),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: familyKeys.members() });
      options?.onSuccess?.(...args);
    },
  });
}

export function useEnableMember<TContext = unknown>(
  options?: UseMutationOptions<void, Error, SetMemberStatusVars, TContext>,
): UseMutationResult<void, Error, SetMemberStatusVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId }) => membersControllerEnableV1(memberId),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: familyKeys.members() });
      options?.onSuccess?.(...args);
    },
  });
}

export interface SetMemberLocaleVars {
  readonly memberId: string;
  readonly data: SetMemberLocaleDto;
}

export function useSetMemberLocale<TContext = unknown>(
  options?: UseMutationOptions<void, Error, SetMemberLocaleVars, TContext>,
): UseMutationResult<void, Error, SetMemberLocaleVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, data }) => membersControllerSetLocaleV1(memberId, data),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: familyKeys.members() });
      options?.onSuccess?.(...args);
    },
  });
}

// --- chore templates ---

/**
 * The household's chore templates (`GET /v1/tasks/templates`) — cursor-paginated
 * (`{ data, nextCursor, hasMore }`); consumers read `result.data`. A household's template set fits
 * in the first page, so the admin table does not page further.
 */
export function useTemplates(
  options?: QueryOpts<TemplatesControllerListV1200>,
): UseQueryResult<TemplatesControllerListV1200, Error> {
  return useQuery({
    queryKey: adminKeys.templates(),
    queryFn: ({ signal }) => templatesControllerListV1(undefined, undefined, signal),
    ...options,
  });
}

/**
 * Create a chore template (`POST /v1/tasks/templates`, `chore:create`). Carries a fresh
 * `Idempotency-Key` per call (mirrors `useCreateLearningGoal`): the route is `@Idempotent` server-side,
 * and the shared mutator's offline outbox (ADR-0018) only queues a failed write when it carries this
 * header — without one, a lost response neither queues offline nor dedupes a lost-response retry, so a
 * resubmitted create would mint a fully independent duplicate chore (with its own paid occurrences).
 */
export function useCreateTemplate<TContext = unknown>(
  options?: UseMutationOptions<CreateTemplateResponse, Error, CreateTemplateDto, TContext>,
): UseMutationResult<CreateTemplateResponse, Error, CreateTemplateDto, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => templatesControllerCreateV1(data, idempotent(newIdempotencyKey())),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: adminKeys.templates() });
      // ADR-0142: the calendar is a projection over ACTIVE templates, so every template write also
      // reshapes day/week/missions for its assignees — refresh the whole task-projection root.
      void qc.invalidateQueries({ queryKey: familyKeys.tasksRoot() });
      options?.onSuccess?.(...args);
    },
  });
}

export interface UpdateTemplateVars {
  readonly templateId: string;
  readonly data: UpdateTemplateDto;
}

export function useUpdateTemplate<TContext = unknown>(
  options?: UseMutationOptions<void, Error, UpdateTemplateVars, TContext>,
): UseMutationResult<void, Error, UpdateTemplateVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, data }) => templatesControllerUpdateV1(templateId, data),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: adminKeys.templates() });
      // A schedule/assignee edit changes the projected calendar (ADR-0142) — see useCreateTemplate.
      void qc.invalidateQueries({ queryKey: familyKeys.tasksRoot() });
      options?.onSuccess?.(...args);
    },
  });
}

export interface TemplateLifecycleVars {
  readonly templateId: string;
}

/** Build a lifecycle (pause/resume/archive) mutation hook bound to one generated endpoint. */
function makeTemplateLifecycle(
  fn: (templateId: string, options?: undefined, signal?: AbortSignal) => Promise<void>,
) {
  return function useTemplateLifecycle<TContext = unknown>(
    options?: UseMutationOptions<void, Error, TemplateLifecycleVars, TContext>,
  ): UseMutationResult<void, Error, TemplateLifecycleVars, TContext> {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: ({ templateId }) => fn(templateId),
      ...options,
      onSuccess: (...args) => {
        void qc.invalidateQueries({ queryKey: adminKeys.templates() });
        // Pause/resume/archive/restore flips whether the template projects occurrences at all
        // (ADR-0142) — see useCreateTemplate.
        void qc.invalidateQueries({ queryKey: familyKeys.tasksRoot() });
        options?.onSuccess?.(...args);
      },
    });
  };
}

export const usePauseTemplate = makeTemplateLifecycle(templatesControllerPauseV1);
export const useResumeTemplate = makeTemplateLifecycle(templatesControllerResumeV1);
export const useArchiveTemplate = makeTemplateLifecycle(templatesControllerArchiveV1);

/**
 * Restore an archived chore template (`POST /v1/tasks/templates/:id/restore`, `chore:delete`):
 * `archived` → `active`, the inverse of {@link useArchiveTemplate}. Re-materialization follows. Fits the
 * lifecycle factory (a no-body POST returning void); invalidates the templates list on success.
 */
export const useRestoreTemplate = makeTemplateLifecycle(templatesControllerRestoreV1);

/**
 * Hard-delete a never-used chore template (`DELETE /v1/tasks/templates/:id`, `chore:delete`). Only
 * succeeds when the chore has no acted-on occurrence (the API 409s with `TASK_TEMPLATE_IN_USE`
 * otherwise) — the read model's `deletable` flag lets the UI offer this only where it will succeed.
 * Invalidates the templates list on success.
 */
export function useDeleteTemplate<TContext = unknown>(
  options?: UseMutationOptions<void, Error, TemplateLifecycleVars, TContext>,
): UseMutationResult<void, Error, TemplateLifecycleVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId }) => templatesControllerRemoveV1(templateId),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: adminKeys.templates() });
      // Deleting a template withdraws its projected cards from the calendar (ADR-0142).
      void qc.invalidateQueries({ queryKey: familyKeys.tasksRoot() });
      options?.onSuccess?.(...args);
    },
  });
}

// --- economy config (admin-only: GET + PUT) ---

/**
 * The household's economy configuration (`GET /v1/economy/config` → `EconomyConfigResponse`):
 * weekly allowance (cents), monthly savings interest (bps), and whether the cash currency mode is
 * enabled. Admin-tier read (`economy:configure`); the API gates it, the UI only mounts it where the
 * user has the permission.
 */
export function useEconomyConfig(
  options?: QueryOpts<EconomyConfigResponse>,
): UseQueryResult<EconomyConfigResponse, Error> {
  return useQuery({
    queryKey: adminKeys.economyConfig(),
    queryFn: ({ signal }) => economyAdminControllerConfigV1(undefined, signal),
    ...options,
  });
}

/**
 * Update the economy configuration (`PUT /v1/economy/config`, admin-only). A config change is
 * CONFIRMED in the UI before submit; on success the config read is invalidated so the form reflects
 * the canonical values. Not money-moving (it sets future allowance/interest), so no idempotency key.
 */
export function useConfigureEconomy<TContext = unknown>(
  options?: UseMutationOptions<void, Error, ConfigureEconomyDto, TContext>,
): UseMutationResult<void, Error, ConfigureEconomyDto, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => economyAdminControllerConfigureV1(data),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: adminKeys.economyConfig() });
      options?.onSuccess?.(...args);
    },
  });
}

// --- achievement bonuses (ADR-0080, admin-only: list/create/update/delete) ---

/**
 * The household's achievement bonuses (`GET /v1/economy/bonuses` → `AchievementBonusResponse[]`) — the
 * coin-earning rules shown on the Ways-to-earn surface. Admin-tier (`economy:configure`).
 */
export function useBonuses(
  options?: QueryOpts<AchievementBonusResponse[]>,
): UseQueryResult<AchievementBonusResponse[], Error> {
  return useQuery({
    queryKey: adminKeys.bonuses(),
    queryFn: ({ signal }) => bonusesControllerListV1(undefined, signal),
    ...options,
  });
}

/**
 * Create an achievement bonus (`POST /v1/economy/bonuses`); invalidates the list on success. Carries
 * a fresh `Idempotency-Key` per call (mirrors `useCreateLearningGoal`): a bonus rule has no natural
 * dedupe key for "was this already added", so without one a lost response risked a duplicate,
 * permanently-active rule on retry, AND the shared mutator's offline outbox (ADR-0018) only queues a
 * failed write when it carries this header — so an add attempted offline was silently NOT queued
 * (unlike every sibling admin mutation here) and just threw straight to the caller.
 */
export function useCreateBonus<TContext = unknown>(
  options?: UseMutationOptions<CreateBonusResponse, Error, CreateBonusDto, TContext>,
): UseMutationResult<CreateBonusResponse, Error, CreateBonusDto, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => bonusesControllerCreateV1(data, idempotent(newIdempotencyKey())),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: adminKeys.bonuses() });
      options?.onSuccess?.(...args);
    },
  });
}

/** Edit an achievement bonus (`PUT /v1/economy/bonuses/:id`); invalidates the list on success. */
export function useUpdateBonus<TContext = unknown>(
  options?: UseMutationOptions<void, Error, { bonusId: string; data: UpdateBonusDto }, TContext>,
): UseMutationResult<void, Error, { bonusId: string; data: UpdateBonusDto }, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bonusId, data }) => bonusesControllerUpdateV1(bonusId, data),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: adminKeys.bonuses() });
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete an achievement bonus (`DELETE /v1/economy/bonuses/:id`); invalidates the list on success. */
export function useDeleteBonus<TContext = unknown>(
  options?: UseMutationOptions<void, Error, string, TContext>,
): UseMutationResult<void, Error, string, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bonusId) => bonusesControllerRemoveV1(bonusId),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: adminKeys.bonuses() });
      options?.onSuccess?.(...args);
    },
  });
}

// --- learning goals (ADR-0080, admin-only: list/create/update/delete per child) ---

/** A child's scheduled learning goals (`GET …/goals/child/:id`). Admin-tier (`learning:author`). */
export function useLearningGoals(
  childId: string,
  options?: QueryOpts<LearningGoalResponse[]>,
): UseQueryResult<LearningGoalResponse[], Error> {
  return useQuery({
    queryKey: adminKeys.learningGoals(childId),
    queryFn: ({ signal }) => learningGoalsControllerListV1(childId, undefined, signal),
    ...options,
  });
}

/** Edit a learning goal — `childId` rides in the vars so it never goes stale in a closure. */
export interface UpdateLearningGoalVars {
  readonly childId: string;
  readonly goalId: string;
  readonly data: UpdateLearningGoalDto;
}

/** Delete a learning goal — `childId` rides in the vars (sibling pattern) for the list invalidation. */
export interface DeleteLearningGoalVars {
  readonly childId: string;
  readonly goalId: string;
}

/**
 * Create a learning goal (`POST …/goals`); invalidates the child's goal list on success. `childId`
 * comes from the mutation body (`CreateLearningGoalDto.childId`) rather than a closure, so the
 * invalidation always targets the child actually written — no stale-capture if the form's child changes.
 *
 * Carries a fresh `Idempotency-Key` per call (mirrors `useCreatePack`/`useSavePack` in the entities
 * layer): a child has no natural dedupe key for "was this goal already added", so without one a lost
 * response would risk a duplicate on retry, AND — the more immediate gap this closes — the shared
 * mutator's offline outbox (ADR-0018) only queues a failed write when it carries this header, so an
 * add attempted offline was silently NOT queued (unlike every sibling admin mutation here) and just
 * threw straight to the caller.
 */
export function useCreateLearningGoal<TContext = unknown>(
  options?: UseMutationOptions<CreateLearningGoalResponse, Error, CreateLearningGoalDto, TContext>,
): UseMutationResult<CreateLearningGoalResponse, Error, CreateLearningGoalDto, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => learningGoalsControllerCreateV1(data, idempotent(newIdempotencyKey())),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: adminKeys.learningGoals(args[1].childId) });
      options?.onSuccess?.(...args);
    },
  });
}

/**
 * Edit a learning goal (`PUT …/goals/:id`); invalidates the child's goal list on success. `childId`
 * is passed through the vars (mirrors `useAdjustBalance`) so the invalidation can't capture a stale id.
 * Carries a fresh `Idempotency-Key` per call — see {@link useCreateLearningGoal}.
 */
export function useUpdateLearningGoal<TContext = unknown>(
  options?: UseMutationOptions<void, Error, UpdateLearningGoalVars, TContext>,
): UseMutationResult<void, Error, UpdateLearningGoalVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ goalId, data }) =>
      learningGoalsControllerUpdateV1(goalId, data, idempotent(newIdempotencyKey())),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: adminKeys.learningGoals(args[1].childId) });
      options?.onSuccess?.(...args);
    },
  });
}

/**
 * Delete a learning goal (`DELETE …/goals/:id`); invalidates the child's goal list on success.
 * `childId` rides in the vars (sibling pattern) so the invalidation never targets a stale child.
 * Carries a fresh `Idempotency-Key` per call — see {@link useCreateLearningGoal}.
 */
export function useDeleteLearningGoal<TContext = unknown>(
  options?: UseMutationOptions<void, Error, DeleteLearningGoalVars, TContext>,
): UseMutationResult<void, Error, DeleteLearningGoalVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ goalId }) =>
      learningGoalsControllerRemoveV1(goalId, idempotent(newIdempotencyKey())),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: adminKeys.learningGoals(args[1].childId) });
      options?.onSuccess?.(...args);
    },
  });
}

export interface AdjustBalanceVars {
  readonly data: AdjustBalanceDto;
  /** Stable per-intent key so an offline replay never double-applies the manual adjustment. */
  readonly idempotencyKey: string;
}

/**
 * Post a manual balance adjustment (`POST /v1/economy/adjustments`, admin-only `balance:adjust`). A
 * MONEY mutation: the UI CONFIRMS before submit and sends an `Idempotency-Key` so a retry/offline
 * replay never double-posts. On success the affected child's balance + ledger and the analytics
 * aggregate are invalidated so every economy view stays consistent (the ledger is append-only —
 * this appends a new compensating entry, never edits one).
 */
export function useAdjustBalance<TContext = unknown>(
  options?: UseMutationOptions<void, Error, AdjustBalanceVars, TContext>,
): UseMutationResult<void, Error, AdjustBalanceVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data, idempotencyKey }) =>
      economyAdminControllerAdjustV1(data, idempotent(idempotencyKey)),
    ...options,
    onSuccess: (...args) => {
      const vars = args[1];
      void qc.invalidateQueries({ queryKey: familyKeys.balance(vars.data.childId) });
      void qc.invalidateQueries({
        queryKey: ['economy', 'ledger', vars.data.childId],
      });
      // Prefix match (not `familyKeys.balanceTrend(childId, from, to)`, which would only invalidate
      // one exact window) — a manual adjustment should stale every cached trend range for this child.
      void qc.invalidateQueries({ queryKey: ['economy', 'balance-trend', vars.data.childId] });
      // Prefix match (not `adminKeys.analytics()`, which defaults its own args to the literal 'all'/
      // 'all' and would never match a windowed month/week cache entry) — stale every cached window.
      void qc.invalidateQueries({ queryKey: ['admin', 'economy', 'analytics'] });
      options?.onSuccess?.(...args);
    },
  });
}

// --- rewards (catalog writes; the read lives in the shared family layer as `useRewards`) ---

/**
 * Create a reward (`POST /v1/economy/rewards`); invalidates the rewards list on success. Carries a
 * fresh `Idempotency-Key` per call (mirrors `useCreateBonus`/`useCreateTemplate`): a reward has no
 * natural dedupe key for "was this already added", so without one a lost response risked a duplicate
 * on retry, AND the shared mutator's offline outbox (ADR-0018) only queues a failed write when it
 * carries this header. A per-call (not per-intent-stable) key is required here specifically —
 * `StarterContentPicker` creates several rewards back-to-back in a loop off ONE hook instance, and a
 * single stable key shared across those calls would make every reward after the first a
 * same-key-different-body 422 instead of its own create.
 */
export function useCreateReward<TContext = unknown>(
  options?: UseMutationOptions<CreateRewardResponse, Error, CreateRewardDto, TContext>,
): UseMutationResult<CreateRewardResponse, Error, CreateRewardDto, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => rewardsControllerCreateV1(data, idempotent(newIdempotencyKey())),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: ['economy', 'rewards'] });
      options?.onSuccess?.(...args);
    },
  });
}

export interface UpdateRewardVars {
  readonly rewardId: string;
  readonly data: UpdateRewardDto;
}

export function useUpdateReward<TContext = unknown>(
  options?: UseMutationOptions<void, Error, UpdateRewardVars, TContext>,
): UseMutationResult<void, Error, UpdateRewardVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rewardId, data }) => rewardsControllerUpdateV1(rewardId, data),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: ['economy', 'rewards'] });
      options?.onSuccess?.(...args);
    },
  });
}

export interface ArchiveRewardVars {
  readonly rewardId: string;
}

export function useArchiveReward<TContext = unknown>(
  options?: UseMutationOptions<void, Error, ArchiveRewardVars, TContext>,
): UseMutationResult<void, Error, ArchiveRewardVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rewardId }) => rewardsControllerArchiveV1(rewardId),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: ['economy', 'rewards'] });
      options?.onSuccess?.(...args);
    },
  });
}

/**
 * Hard-delete a reward (`DELETE /v1/economy/rewards/:id`, `reward:manage:own` — own-vs-any resolved
 * server-side, so a Relative may delete only their own sponsored rewards). Only offered when the reward's
 * `deletable` flag is true; a reward with redemption history is rejected with 409 `REWARD_IN_USE` (archive
 * it instead). Invalidates every rewards view.
 */
export function useDeleteReward<TContext = unknown>(
  options?: UseMutationOptions<void, Error, ArchiveRewardVars, TContext>,
): UseMutationResult<void, Error, ArchiveRewardVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rewardId }) => rewardsControllerRemoveV1(rewardId),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: ['economy', 'rewards'] });
      options?.onSuccess?.(...args);
    },
  });
}

/**
 * Restore an archived reward (`POST /v1/economy/rewards/:id/restore`, `reward:manage:any`): `archived`
 * → `active`, re-publishing it to the kid shop. The inverse of {@link useArchiveReward}. Invalidates
 * every rewards view (the catalog re-includes it + the shop regains it).
 */
export function useRestoreReward<TContext = unknown>(
  options?: UseMutationOptions<void, Error, ArchiveRewardVars, TContext>,
): UseMutationResult<void, Error, ArchiveRewardVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rewardId }) => rewardsControllerRestoreV1(rewardId),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: ['economy', 'rewards'] });
      options?.onSuccess?.(...args);
    },
  });
}

/**
 * The household's rewards awaiting a Parent's approval (ADR-0050). A Relative's newly-created reward is
 * born `pending` and hidden from the kid shop; this feeds the guardian Inbox so a Parent can approve it
 * (publishing it to the shop) or deny it (an archive). Fetched with `includeArchived` (the only way to
 * surface non-active rewards) and filtered client-side to the `pending` ones — gate the call on
 * `reward:manage:any` (only a Parent may approve) so a Relative never fires it.
 */
export function usePendingRewards(
  options?: QueryOpts<RewardResponse[]>,
): UseQueryResult<RewardResponse[], Error> {
  return useQuery({
    queryKey: adminKeys.pendingRewards(),
    queryFn: ({ signal }) => rewardsControllerListV1({ includeArchived: true }, undefined, signal),
    select: (rewards) => rewards.filter((r) => r.status === 'pending'),
    ...options,
  });
}

/**
 * The household's full reward catalog for the command center — active AND archived (`includeArchived`,
 * admin-only). `pending` rewards are filtered out: they live in the Inbox as approval decisions, not in
 * the catalog. The kid shop uses `useRewards` (active-only); this is the management view, which must
 * still surface retired rewards so a Parent can restore them ({@link useRestoreReward}). Shares the
 * `["economy","rewards"]` prefix so archive/restore/edit invalidations refetch it.
 */
export function useRewardCatalog(
  options?: QueryOpts<RewardResponse[]>,
): UseQueryResult<RewardResponse[], Error> {
  return useQuery({
    queryKey: adminKeys.rewardCatalog(),
    queryFn: ({ signal }) => rewardsControllerListV1({ includeArchived: true }, undefined, signal),
    select: (rewards) => rewards.filter((r) => r.status !== 'pending'),
    ...options,
  });
}

export interface ApproveRewardVars {
  readonly rewardId: string;
}

/**
 * Approve a pending reward (`POST /v1/economy/rewards/:id/approve`, `reward:manage:any`): `pending` →
 * `active`, publishing it to the shop. Invalidates every rewards view (the pending queue empties + the
 * shop gains the reward). Denying a pending reward is just `useArchiveReward` — no separate endpoint.
 */
export function useApproveReward<TContext = unknown>(
  options?: UseMutationOptions<void, Error, ApproveRewardVars, TContext>,
): UseMutationResult<void, Error, ApproveRewardVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rewardId }) => rewardsControllerApproveV1(rewardId),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: ['economy', 'rewards'] });
      options?.onSuccess?.(...args);
    },
  });
}

// --- redemptions (household queue + decisions) ---

/**
 * The household's redemption requests (`GET /v1/economy/redemptions` → cursor `Page`); consumers
 * read `result.data`. Drives the pending-redemptions banner + table. Admin-tier read gated by
 * `redemption:grant:*` (the API scopes which the user may decide).
 *
 * `params` forwards the server-side filters (`status`/`childId`/`limit`/…). The Inbox MUST filter
 * to `status: "requested"` server-side — without it the endpoint returns ONE recency-ordered page
 * across ALL statuses, so a still-pending request older than a pageful of decided history would
 * silently vanish from the queue. The params join the query key (the decision mutations invalidate
 * by the `adminKeys.redemptions()` prefix, which still matches every params variant).
 */
export function useRedemptions(
  params?: RedemptionsControllerListV1Params,
  options?: QueryOpts<RedemptionsControllerListV1200>,
): UseQueryResult<RedemptionsControllerListV1200, Error> {
  return useQuery({
    queryKey: params ? ([...adminKeys.redemptions(), params] as const) : adminKeys.redemptions(),
    queryFn: ({ signal }) => redemptionsControllerListV1(params, undefined, signal),
    ...options,
  });
}

export interface DecideRedemptionVars {
  readonly redemptionId: string;
  /** The affected child, so the decision can invalidate their balance/ledger on a grant. */
  readonly childId: string;
  readonly note?: string;
  /** Stable per-intent key — granting debits coins, so an offline replay must not double-decide. */
  readonly idempotencyKey: string;
}

/** Build a grant/deny mutation hook bound to one generated endpoint (both are idempotent). */
function makeRedemptionDecision(
  fn: (
    redemptionId: string,
    body: DecideRedemptionDto,
    options?: { headers: Record<string, string> },
  ) => Promise<void>,
) {
  return function useRedemptionDecision<TContext = unknown>(
    options?: UseMutationOptions<void, Error, DecideRedemptionVars, TContext>,
  ): UseMutationResult<void, Error, DecideRedemptionVars, TContext> {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: ({ redemptionId, note, idempotencyKey }) => {
        const body: DecideRedemptionDto = note ? { note } : {};
        return fn(redemptionId, body, idempotent(idempotencyKey));
      },
      ...options,
      onSuccess: (...args) => {
        const vars = args[1];
        void qc.invalidateQueries({ queryKey: adminKeys.redemptions() });
        void qc.invalidateQueries({ queryKey: familyKeys.balance(vars.childId) });
        void qc.invalidateQueries({ queryKey: ['economy', 'ledger', vars.childId] });
        // Granting debits the append-only ledger, so the balance-trend sparkline (reconstructed from
        // the ledger) goes stale too — invalidate it as a prefix match, mirroring useAdjustBalance.
        void qc.invalidateQueries({ queryKey: ['economy', 'balance-trend', vars.childId] });
        // A grant is a ledger spend that also feeds the earn-vs-spend / spend-by-category analytics
        // aggregate — invalidate that prefix too, mirroring useAdjustBalance.
        void qc.invalidateQueries({ queryKey: ['admin', 'economy', 'analytics'] });
        options?.onSuccess?.(...args);
      },
    });
  };
}

export const useGrantRedemption = makeRedemptionDecision(redemptionsControllerGrantV1);
export const useDenyRedemption = makeRedemptionDecision(redemptionsControllerDenyV1);

// --- privilege holds (place + lift; the per-child read lives in family as `useActiveHolds`) ---

export interface PlaceHoldVars {
  readonly data: PlaceHoldDto;
  /** Stable per-intent key (holds gate redemption; a privilege mutation is replay-safe). */
  readonly idempotencyKey: string;
}

/**
 * Place a privilege hold (`POST /v1/economy/holds`). The comment is MANDATORY (UI-validated AND
 * API-enforced, 1–200 chars). A hold gates redemption only — it never deducts points. On success the
 * affected child's holds list + the rewards catalog (the `locked` flag) are invalidated. Carries an
 * idempotency key so an offline replay never places a duplicate hold.
 */
export function usePlaceHold<TContext = unknown>(
  options?: UseMutationOptions<PlaceHoldResponse, Error, PlaceHoldVars, TContext>,
): UseMutationResult<PlaceHoldResponse, Error, PlaceHoldVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data, idempotencyKey }) =>
      holdsControllerPlaceV1(data, idempotent(idempotencyKey)),
    ...options,
    onSuccess: (...args) => {
      const vars = args[1];
      void qc.invalidateQueries({ queryKey: familyKeys.holds(vars.data.childId) });
      void qc.invalidateQueries({ queryKey: familyKeys.householdHolds() });
      void qc.invalidateQueries({ queryKey: ['economy', 'rewards'] });
      options?.onSuccess?.(...args);
    },
  });
}

export interface LiftHoldVars {
  readonly holdId: string;
  /** The affected child, so lifting can invalidate their holds list + the rewards catalog. */
  readonly childId: string;
  /** Stable per-intent key — lifting is `@Idempotent`, so an offline replay never re-applies. */
  readonly idempotencyKey: string;
}

/**
 * Lift a privilege hold (`POST /v1/economy/holds/:holdId/lift`). A hold gates redemption only — it
 * never deducted points, so lifting just re-opens the reward/category. On success the affected child's
 * holds list + the rewards catalog (the `locked` flag) are invalidated. Carries an idempotency key so
 * an offline replay never surfaces a spurious "already lifted" FSM 4xx.
 */
export function useLiftHold<TContext = unknown>(
  options?: UseMutationOptions<void, Error, LiftHoldVars, TContext>,
): UseMutationResult<void, Error, LiftHoldVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ holdId, idempotencyKey }) =>
      holdsControllerLiftV1(holdId, idempotent(idempotencyKey)),
    ...options,
    onSuccess: (...args) => {
      const vars = args[1];
      void qc.invalidateQueries({ queryKey: familyKeys.holds(vars.childId) });
      // Also refresh the household-wide "what's paused" banner, which reads a separate key — otherwise it
      // keeps showing the just-lifted hold as active until an unrelated refetch. Mirrors usePlaceHold.
      void qc.invalidateQueries({ queryKey: familyKeys.householdHolds() });
      void qc.invalidateQueries({ queryKey: ['economy', 'rewards'] });
      options?.onSuccess?.(...args);
    },
  });
}

// --- household settings (admin Settings screen) ---

/**
 * The caller's household display + formatting settings (`GET /v1/household` → `HouseholdResponse`:
 * name, timezone, defaultLocale, currency). Readable by any member (`household:read`) so the Settings
 * screen can show the current values; editing is gated separately on `household:manage`.
 */
export function useHousehold(
  options?: QueryOpts<HouseholdResponse>,
): UseQueryResult<HouseholdResponse, Error> {
  return useQuery({
    queryKey: adminKeys.household(),
    queryFn: ({ signal }) => householdControllerGetV1(undefined, signal),
    ...options,
  });
}

/**
 * Update the household settings (`PATCH /v1/household`, admin-only `household:manage`). A partial
 * update: only provided fields change. CONFIRMED in the UI before submit; on success the household
 * read is invalidated so every consumer (formatting, the Settings form) reflects the canonical values.
 * Not money-moving, so no idempotency key.
 */
export function useUpdateHousehold<TContext = unknown>(
  options?: UseMutationOptions<HouseholdResponse, Error, UpdateHouseholdDto, TContext>,
): UseMutationResult<HouseholdResponse, Error, UpdateHouseholdDto, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => householdControllerUpdateV1(data),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: adminKeys.household() });
      options?.onSuccess?.(...args);
    },
  });
}

// --- habits (guardian management: list household habits + create/edit/archive) ---

/**
 * Page size for the habits auto-follow below — the API's `MAX_PAGE_LIMIT`
 * (`apps/api/src/common/pagination/cursor.ts`). Using the cap minimises round-trips: a household with
 * ≤100 habits resolves in a single request, matching the old single-page behaviour.
 */
const HABITS_PAGE_LIMIT = 100;

/**
 * Fetch EVERY habit in the household by following the keyset cursor until `hasMore` is false, then
 * return one collapsed page (`{ data: <all rows>, nextCursor: null, hasMore: false }`). Exported for
 * unit testing; consumers use {@link useHabits}.
 *
 * Without this, a bare first-page fetch silently drops every habit past the first page — the admin
 * habits table AND the guardian calendar fill list (which filters this roster client-side to decide
 * which habits are due on a day) would just never see them once a household crosses one page.
 *
 * The `for` bound is a defensive backstop (100 pages × 100 = 10k habits) so a server that never
 * clears `hasMore`/`nextCursor` can't spin forever; a real household never approaches it.
 */
export async function fetchAllHabits(signal?: AbortSignal): Promise<HabitsControllerListV1200> {
  const data: HabitResponse[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < 100; page++) {
    const res = await habitsControllerListV1(
      { limit: HABITS_PAGE_LIMIT, cursor },
      undefined,
      signal,
    );
    data.push(...res.data);
    if (!res.hasMore || !res.nextCursor) break;
    cursor = res.nextCursor;
  }
  return { data, nextCursor: null, hasMore: false };
}

/**
 * The household's habits (`GET /v1/habits` → cursor `Page` `{ data, nextCursor, hasMore }`);
 * consumers read `result.data`. Each `HabitResponse` carries its child, schedule (daily /
 * weekly_days + ISO weekdays), status (active/archived) + icon. Drives the admin Habits table AND
 * the guardian calendar fill list, and feeds the reward/bonus/quest habit pickers. Gated in the UI
 * on `habit:create`/`habit:manage`; the API enforces regardless.
 *
 * A single API page is capped at 100 rows, but a household's active-habit count (across all children)
 * can exceed that — so this auto-follows the cursor via {@link fetchAllHabits} and returns every
 * habit as one collapsed page (`nextCursor: null`, `hasMore: false`). Consumers only read `.data`, so
 * the collapsed page is a drop-in for the prior single-page shape.
 */
export function useHabits(
  options?: QueryOpts<HabitsControllerListV1200>,
): UseQueryResult<HabitsControllerListV1200, Error> {
  return useQuery({
    queryKey: adminKeys.habits(),
    queryFn: ({ signal }) => fetchAllHabits(signal),
    ...options,
  });
}

/**
 * A single habit's detail (`GET /v1/habits/:habitId` → `HabitDetailResponse`): the habit plus its
 * current/best streak and recent check-ins. Optional surface (per-child streak in the admin list);
 * gated on `habit:manage`. Enabled only when a `habitId` is supplied.
 */
export function useHabitDetail(
  habitId: string,
  options?: QueryOpts<HabitDetailResponse>,
): UseQueryResult<HabitDetailResponse, Error> {
  return useQuery({
    queryKey: adminKeys.habit(habitId),
    queryFn: ({ signal }) => habitsControllerGetV1(habitId, undefined, signal),
    enabled: !!habitId,
    ...options,
  });
}

/**
 * Invalidate every habit view after a create/edit/archive: the admin list + the child's "today"
 * day view (a schedule/name/icon change or an archive changes what the kid sees). The today view is
 * self-scoped per child, so the broad `["habits","today"]` key is invalidated regardless of which
 * child the habit belongs to (only the affected child's mounted query refetches).
 */
function invalidateHabits(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: adminKeys.habits() });
  void qc.invalidateQueries({ queryKey: familyKeys.habitsToday() });
}

/**
 * Create a habit (`POST /v1/habits` → `{ habitId }`, `habit:create`). Maps 1:1 to `CreateHabitDto`:
 * the target child, name (1–80, USER content — never translated), icon key, repeatType (daily /
 * weekly_days) + ISO weekdays for weekly. Not money-moving, but still carries a fresh
 * `Idempotency-Key` per call (mirrors `useCreateLearningGoal`): the route is `@Idempotent`
 * server-side, and the shared mutator's offline outbox (ADR-0018) only queues a failed write when it
 * carries this header — without one, a lost response neither queues offline nor dedupes a
 * lost-response retry, so a resubmitted create would mint a fully independent duplicate habit (with
 * its own streak/check-in history). On success the habit list + the child's today view are
 * invalidated so both surfaces reconcile.
 */
export function useCreateHabit<TContext = unknown>(
  options?: UseMutationOptions<CreateHabitResponse, Error, CreateHabitDto, TContext>,
): UseMutationResult<CreateHabitResponse, Error, CreateHabitDto, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => habitsControllerCreateV1(data, idempotent(newIdempotencyKey())),
    ...options,
    onSuccess: (...args) => {
      invalidateHabits(qc);
      options?.onSuccess?.(...args);
    },
  });
}

export interface UpdateHabitVars {
  readonly habitId: string;
  readonly data: UpdateHabitDto;
}

/**
 * Edit a habit (`PATCH /v1/habits/:habitId`, `habit:manage`). A partial update: name / icon /
 * repeatType / weekDays. On success the list + the child's today view are invalidated.
 */
export function useUpdateHabit<TContext = unknown>(
  options?: UseMutationOptions<void, Error, UpdateHabitVars, TContext>,
): UseMutationResult<void, Error, UpdateHabitVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ habitId, data }) => habitsControllerUpdateV1(habitId, data),
    ...options,
    onSuccess: (...args) => {
      invalidateHabits(qc);
      options?.onSuccess?.(...args);
    },
  });
}

export interface ArchiveHabitVars {
  readonly habitId: string;
}

/**
 * Archive a habit (`POST /v1/habits/:habitId/archive`, `habit:manage`). Archiving stops it from
 * appearing in the child's day view; the streak history is preserved (never deleted — kind). On
 * success the list + the child's today view are invalidated.
 */
export function useArchiveHabit<TContext = unknown>(
  options?: UseMutationOptions<void, Error, ArchiveHabitVars, TContext>,
): UseMutationResult<void, Error, ArchiveHabitVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ habitId }) => habitsControllerArchiveV1(habitId),
    ...options,
    onSuccess: (...args) => {
      invalidateHabits(qc);
      options?.onSuccess?.(...args);
    },
  });
}

export interface DeleteHabitVars {
  readonly habitId: string;
}

/**
 * Hard-delete a never-checked-in habit (`DELETE /v1/habits/:habitId`, `habit:delete`). Only offered when
 * the habit's `deletable` flag is true; the server rejects a habit with check-in history with 409
 * `HABIT_IN_USE` (archive it instead). On success the list + the child's today view are invalidated.
 */
export function useDeleteHabit<TContext = unknown>(
  options?: UseMutationOptions<void, Error, DeleteHabitVars, TContext>,
): UseMutationResult<void, Error, DeleteHabitVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ habitId }) => habitsControllerRemoveV1(habitId),
    ...options,
    onSuccess: (...args) => {
      invalidateHabits(qc);
      options?.onSuccess?.(...args);
    },
  });
}
